import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  CheckCircle, Circle, Envelope, Phone, Trash, ArrowLeft,
  Fire, Warning, Sparkle,
} from "@phosphor-icons/react";
import BuzzLogo from "../components/BuzzLogo";

/**
 * Super-Admin → Beta Applications
 *
 * Every submitted /signup application shows up here with its four qualification
 * answers + a "Mark contacted" toggle so the founder can track which applicants
 * they've personally emailed within the 24-hour promise on the signup page.
 */

const TOOL_LABELS = {
  none:      "Nothing — verbal only",
  clipboard: "Pen & paper",
  waitwhile: "Waitwhile",
  yelp:      "Yelp Waitlist",
  opentable: "OpenTable",
  toast:     "Toast Waitlist",
  other:     "Something else",
};
const CALLS_LABELS = {
  under_5:    "<5 / week",
  "5_to_15":  "5–15 / week",
  "15_to_30": "15–30 / week",
  over_30:    "30+ / week",
  unsure:     "Not sure",
};
const RESTAURANT_LABELS = {
  yes:                "✅ Yes — restaurant",
  not_yet:            "🟡 Planning to open",
  different_business: "❌ Different business",
};

const relTime = (iso) => {
  if (!iso) return "—";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
};

export default function AdminBetaApplications() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("uncontacted"); // uncontacted | all | contacted

  const load = () => {
    api.get("/admin/beta_applications")
      .then((r) => setRows(r.data.applications))
      .catch((e) => {
        toast.error(e.response?.status === 403 ? "Admin access only" : "Couldn't load applications");
        setRows([]);
      });
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    if (filter === "all") return rows;
    if (filter === "uncontacted") return rows.filter((r) => !r.contacted);
    return rows.filter((r) => r.contacted);
  }, [rows, filter]);

  const markContacted = async (a, contacted) => {
    try {
      await api.patch(`/admin/beta_applications/${a.id}`, { contacted });
      setRows((prev) => prev.map((r) => (r.id === a.id ? { ...r, contacted, contacted_at: contacted ? new Date().toISOString() : null } : r)));
      toast.success(contacted ? "Marked contacted" : "Marked as uncontacted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const delRow = async (a) => {
    if (!window.confirm(`Delete beta application from ${a.business_name}? This does NOT delete their account if they finished signup.`)) return;
    try {
      await api.delete(`/admin/beta_applications/${a.id}`);
      setRows((prev) => prev.filter((r) => r.id !== a.id));
      toast.success("Application deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const counts = useMemo(() => {
    if (!rows) return { total: 0, uncontacted: 0, contacted: 0 };
    return {
      total: rows.length,
      uncontacted: rows.filter((r) => !r.contacted).length,
      contacted: rows.filter((r) => r.contacted).length,
    };
  }, [rows]);

  return (
    <div className="min-h-screen bg-[#F7F6F2] font-body">
      <header className="border-b border-[#E1DFD5] px-6 py-4 bg-white flex items-center gap-4">
        <Link to="/admin/customers" className="text-sm text-[#5C6B61] hover:text-[#121914] flex items-center gap-1" data-testid="back-to-cockpit">
          <ArrowLeft size={14} /> Cockpit
        </Link>
        <div className="w-8 h-8 rounded-lg bg-[#0E1512] flex items-center justify-center"><BuzzLogo size={20} /></div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#D96C5B] font-bold">Super-Admin</div>
          <h1 className="font-heading text-lg font-extrabold text-[#121914]">Beta Applications</h1>
        </div>
      </header>

      <section className="px-6 py-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            {[
              { key: "uncontacted", label: `Needs reply (${counts.uncontacted})`, icon: <Fire size={14} weight="fill" /> },
              { key: "contacted",   label: `Contacted (${counts.contacted})`,     icon: <CheckCircle size={14} weight="fill" /> },
              { key: "all",         label: `All (${counts.total})`,               icon: <Sparkle size={14} weight="fill" /> },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`beta-filter-${f.key}`}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${
                  filter === f.key
                    ? "bg-[#0E1512] text-white"
                    : "bg-white text-[#5C6B61] border border-[#E1DFD5] hover:border-[#5C6B61]"
                }`}
              >
                {f.icon} {f.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-[#5C6B61]">Newest applications first. You promised applicants a founder reply within 24 hours.</p>
        </div>

        {rows === null && <p className="text-sm text-[#5C6B61]">Loading…</p>}
        {rows !== null && filtered.length === 0 && (
          <div className="paper-card p-8 text-center" data-testid="beta-empty-state">
            <div className="w-12 h-12 rounded-full bg-[#C2D1C1]/40 flex items-center justify-center mx-auto mb-3">
              <CheckCircle size={22} color="#1E3A2F" weight="fill" />
            </div>
            <h3 className="font-heading text-xl font-extrabold text-[#121914] mb-1">
              {filter === "uncontacted" ? "Inbox zero." : filter === "contacted" ? "No contacted applicants yet." : "No beta applications yet."}
            </h3>
            <p className="text-sm text-[#5C6B61]">
              {filter === "uncontacted"
                ? "You've replied to every applicant. Well done."
                : "As restaurants apply, they'll show up here for you to reach out to."}
            </p>
          </div>
        )}

        <div className="space-y-3" data-testid="beta-applications-list">
          {filtered.map((a) => {
            const restaurantOk = a.is_restaurant === "yes";
            const feedbackOk = a.wants_feedback_channel;
            return (
              <div
                key={a.id}
                className={`paper-card p-5 grid md:grid-cols-[1fr_auto] gap-4 ${a.contacted ? "opacity-70" : ""}`}
                data-testid={`beta-row-${a.id}`}
              >
                <div>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-heading text-lg font-extrabold text-[#121914]">{a.business_name}</h3>
                    <span className="text-[10px] font-mono text-[#5C6B61]">{relTime(a.ts)}</span>
                    {!restaurantOk && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#78350F]">
                        <Warning size={10} className="inline mr-0.5" weight="fill" /> Not a restaurant
                      </span>
                    )}
                    {!feedbackOk && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#78350F]">
                        No feedback commitment
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[#121914] mb-3">
                    {a.owner_name} · <a href={`mailto:${a.email}`} className="text-[#0E1512] underline decoration-[#F5C543] decoration-2 underline-offset-2">{a.email}</a>
                  </p>
                  <div className="grid sm:grid-cols-3 gap-3 text-xs">
                    <div>
                      <div className="overline mb-0.5">Type</div>
                      <div className="text-[#121914]">{RESTAURANT_LABELS[a.is_restaurant] || a.is_restaurant || "—"}</div>
                    </div>
                    <div>
                      <div className="overline mb-0.5">Missed calls/wk</div>
                      <div className="text-[#121914]">{CALLS_LABELS[a.missed_calls_per_week] || a.missed_calls_per_week || "—"}</div>
                    </div>
                    <div>
                      <div className="overline mb-0.5">Current tool</div>
                      <div className="text-[#121914]">{TOOL_LABELS[a.current_waitlist_tool] || a.current_waitlist_tool || "—"}</div>
                    </div>
                  </div>
                  {a.contacted && a.contacted_at && (
                    <p className="text-[11px] text-[#1E3A2F] mt-3 font-mono">
                      <CheckCircle size={11} className="inline" weight="fill" /> Contacted {relTime(a.contacted_at)}
                    </p>
                  )}
                </div>
                <div className="flex md:flex-col items-start gap-2">
                  <a
                    href={`mailto:${a.email}?subject=Welcome to BuzzerAI — ${a.business_name}&body=Hi ${a.owner_name.split(" ")[0] || ""},%0D%0A%0D%0AThanks for applying to the BuzzerAI beta.`}
                    className="btn-ghost !py-1.5 !px-2.5 text-[11px]"
                    data-testid={`beta-email-${a.id}`}
                  >
                    <Envelope size={12} /> Email
                  </a>
                  <button
                    onClick={() => markContacted(a, !a.contacted)}
                    className={`btn-ghost !py-1.5 !px-2.5 text-[11px] ${a.contacted ? "!text-[#5C6B61]" : "!text-[#1E3A2F]"}`}
                    data-testid={`beta-mark-${a.id}`}
                  >
                    {a.contacted ? <Circle size={12} /> : <CheckCircle size={12} weight="fill" />}
                    {a.contacted ? "Undo" : "Mark contacted"}
                  </button>
                  <button
                    onClick={() => delRow(a)}
                    className="btn-ghost !py-1.5 !px-2.5 text-[11px] !text-[#A5382A]"
                    data-testid={`beta-delete-${a.id}`}
                  >
                    <Trash size={12} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
